import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "#lib/utils";

// A module's tile: decoration beside a word that names it, so it is hidden from assistive tech.
const iconTileVariants = cva(
  "tile inline-flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:size-[var(--icon-size-sm)]",
  {
    variants: {
      hue: {
        red: "[--tile-from:var(--tile-red-from)] [--tile-to:var(--tile-red-to)]",
        amber:
          "[--tile-from:var(--tile-amber-from)] [--tile-to:var(--tile-amber-to)]",
        blue: "[--tile-from:var(--tile-blue-from)] [--tile-to:var(--tile-blue-to)]",
        green:
          "[--tile-from:var(--tile-green-from)] [--tile-to:var(--tile-green-to)]",
        violet:
          "[--tile-from:var(--tile-violet-from)] [--tile-to:var(--tile-violet-to)]",
        teal: "[--tile-from:var(--tile-teal-from)] [--tile-to:var(--tile-teal-to)]",
        gray: "[--tile-from:var(--tile-gray-from)] [--tile-to:var(--tile-gray-to)]",
        stone:
          "[--tile-from:var(--tile-stone-from)] [--tile-to:var(--tile-stone-to)]",
      },
      size: {
        md: "size-[var(--tile-size)] rounded-sm",
        lg: "size-[var(--tile-size-lg)] rounded-row [&_svg]:size-[var(--icon-size)]",
        xl: "size-[var(--tile-size-xl)] rounded-group [&_svg]:size-[var(--icon-size-xl)]",
        state:
          "size-[var(--tile-size-state)] rounded-row [&_svg]:size-[var(--icon-size-nav)]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

function IconTile({
  className,
  hue,
  size = "md",
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof iconTileVariants> & {
    hue: NonNullable<VariantProps<typeof iconTileVariants>["hue"]>;
  }) {
  return (
    <span
      aria-hidden="true"
      data-slot="icon-tile"
      className={cn(iconTileVariants({ hue, size }), className)}
      {...props}
    />
  );
}

export { IconTile };
